// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useState, useCallback, useEffect } from 'react'
import { AuthenticationProvider } from '@axa-fr/react-oidc-context'
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'

import { getCustomerAccount } from './utils/feature-flag-utils'
import getOIDCConfig from './oidc-config'
import { enableOidcLogs } from './utils/log-util'
import { NotAuthorized, NotAuthenticated } from './pages/acct-onboarding'
import { CCSContextProvider } from './context/ccs-context'
import StubbedAuthProvider from './context/stubbed-auth-context'
import Genie from './Genie'
import { ContextualHelp } from './commoncomponents/contextual-help-layout/ContextualHelp'
import LazyLoading from './commoncomponents/LazyLoading/LazyLoading'

const RightSideBar = () => {
  const [isCustAccountLoaded, setIsCustAccountLoaded] = useState(false)
  const LDFlags = useFlags()
  const ldClient = useLDClient()
  const genieLDFlag = LDFlags['glcp-genie-beta']
  const [isGenieEnabled, setIsGenieEnabled] = useState(false)

  const [isContextHelpVisible, setIsContextHelpVisible] = useState(false)
  const [isGenieVisible, setIsGenieVisible] = useState(false)

  const { authorityURL } = window.$settings
  const OIDCConfig = getOIDCConfig(authorityURL)

  // handle contextual help icon click event
  const onContextHelp = useCallback(() => {
    // if LD Flag is not enabled keeping default behavior
    if (!isGenieEnabled) {
      return
    }
    const bodyClasses = document.body.className.split(' ')
    const hasHelpOpen = bodyClasses.includes('helpOpen') // check if sidebar is visible
    if (isContextHelpVisible && hasHelpOpen) {
      // if help is already visible and user clicked it we need to hide it
      setIsContextHelpVisible(false)
      document.body.className = bodyClasses.filter(
        (item) => item !== 'helpOpen'
      )
    } else {
      setIsContextHelpVisible(true)
      setIsGenieVisible(false)
      // since help is hidden, make it visible
      if (!hasHelpOpen) {
        document.body.className = [...bodyClasses, 'helpOpen'].join(' ')
      }
    }
  }, [isContextHelpVisible, isGenieEnabled])

  useEffect(() => {
    window.addEventListener('contextHelp', onContextHelp)
    window.addEventListener('context-help', onContextHelp)
    return () => {
      window.removeEventListener('contextHelp', onContextHelp)
      window.removeEventListener('context-help', onContextHelp)
    }
  }, [onContextHelp])

  const handleLaunchGenie = useCallback(() => {
    const bodyClasses = document.body.className.split(' ')
    const hasHelpOpen = bodyClasses.includes('helpOpen')

    // if genie is visible hide it
    if (isGenieVisible && hasHelpOpen) {
      setIsGenieVisible(false)
      document.body.className = bodyClasses.filter(
        (item) => item !== 'helpOpen'
      )
    } else {
      // genie is not visible show it and hide contextual help
      setIsGenieVisible(true)
      setIsContextHelpVisible(false)
      if (!hasHelpOpen) {
        document.body.className = [...bodyClasses, 'helpOpen'].join(' ')
      }
    }
  }, [isGenieVisible])

  useEffect(() => {
    // Add event listener

    window.addEventListener('launchGenie', handleLaunchGenie)

    // Clean up
    return () => {
      window.removeEventListener('launchGenie', handleLaunchGenie)
    }
  }, [handleLaunchGenie])

  // need to check if account loaded, then load LD flags and genie
  useEffect(() => {
    if (isCustAccountLoaded) {
      return null
    }

    let checkInterval
    const checkIsCustAccountLoaded = () => {
      const custAccountLoaded = getCustomerAccount()
      if (custAccountLoaded) {
        setIsCustAccountLoaded(true)
        // since this is the help div, need to update flag values for given WS
        if (window.REACT_APP_ENABLE_LD && !window.Cypress) {
          ldClient?.identify({ key: custAccountLoaded?.platform_customer_id })
        }
        // since account is loaded clean interval and listener
        if (checkInterval) {
          clearInterval(checkInterval)
        }
        window.removeEventListener('popstate', checkIsCustAccountLoaded)
      }
    }

    // Use interval and add listener to check if account is loaded
    checkInterval = setInterval(checkIsCustAccountLoaded, 1000)
    window.addEventListener('popstate', checkIsCustAccountLoaded)

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval)
      }
      window.removeEventListener('popstate', checkIsCustAccountLoaded)
    }
  }, [isCustAccountLoaded, ldClient])

  useEffect(() => {
    setIsGenieEnabled(genieLDFlag)
  }, [genieLDFlag])

  // if Genie flag is OFF keep default behavior
  if (!isGenieEnabled) {
    return <ContextualHelp proxyOverride={process.env.REACT_APP_HELP_PROXY} />
  }

  return (
    <>
      {isCustAccountLoaded && (
        <div
          style={{
            display: !isGenieVisible ? 'none' : 'block',
            pointerEvents: !isGenieVisible ? 'none' : 'auto'
          }}
        >
          <CCSContextProvider>
            {process.env.REACT_APP_PROXY_MIRAGE ? (
              <StubbedAuthProvider>
                <Genie />
              </StubbedAuthProvider>
            ) : (
              <AuthenticationProvider
                configuration={OIDCConfig}
                loggerLevel={enableOidcLogs()}
                isEnabled
                callbackComponentOverride={() => {
                  return <LazyLoading />
                }}
                notAuthenticated={NotAuthenticated}
                notAuthorized={NotAuthorized}
              >
                <Genie />
              </AuthenticationProvider>
            )}
          </CCSContextProvider>
        </div>
      )}
      {isContextHelpVisible && (
        <ContextualHelp proxyOverride={process.env.REACT_APP_HELP_PROXY} />
      )}
    </>
  )
}

export default RightSideBar
